export function isProrated(activationDate, nextInvoiceDate, differenceQuote = false, account_status = null) {
   // If this is a new account (not a Subscriber) and not a difference quote, don't prorate
   const isNewAccount = account_status !== 'Subscriber'
   if (isNewAccount && !differenceQuote) {
      return false
   }
   // Date of activation of products.
   // All date math below uses the UTC getters/setters so that calendar dates parsed from
   // 'YYYY-MM-DD' strings (which Date() always parses as UTC midnight) aren't shifted by a
   // day when the host machine's local timezone is behind UTC.
   let dateOfActivation = new Date(activationDate)
   dateOfActivation.setUTCDate(dateOfActivation.getUTCDate())

   // Date of next invoice. Takes into account if the activation date is after the next invoice date. If so, uses the next months billing date for the calculation.
   let dateOfFutureInvoice = new Date(nextInvoiceDate)
   if (dateOfActivation > dateOfFutureInvoice) {
      dateOfFutureInvoice.setUTCMonth(
         dateOfFutureInvoice.getUTCMonth() +
            Math.max(
               1,
               dateOfActivation.getUTCMonth() -
                  dateOfFutureInvoice.getUTCMonth() +
                  dateOfActivation.getUTCDate() >
                  dateOfFutureInvoice.getUTCDate()
                  ? 1
                  : 0,
            ),
      )
   }

   // Find date of last invoice. If the day of the month of billing date is greater than the last day of the (I.E someone is billed on the 30th normally, but it is February and the onth only has 28 days), then use the last day of the month instead.
   let dateOfLastInvoice = new Date(dateOfFutureInvoice)
   if (
      new Date(
         Date.UTC(
            dateOfFutureInvoice.getUTCFullYear(),
            dateOfFutureInvoice.getUTCMonth() - 1,
            dateOfFutureInvoice.getUTCDate(),
         ),
      ) >
      new Date(
         Date.UTC(
            dateOfFutureInvoice.getUTCFullYear(),
            dateOfFutureInvoice.getUTCMonth() - 1,
         ),
      )
   ) {
      dateOfLastInvoice.setUTCFullYear(dateOfFutureInvoice.getUTCFullYear())
      dateOfLastInvoice.setUTCMonth(dateOfFutureInvoice.getUTCMonth() - 1)
      dateOfLastInvoice.setUTCDate(dateOfFutureInvoice.getUTCDate())
      // If the billing date would have been past the last day of the month (IE into the next month), set the billing date to the last day of the month.
      if (dateOfLastInvoice.getUTCMonth() == dateOfFutureInvoice.getUTCMonth()) {
         dateOfLastInvoice.setUTCMonth(dateOfLastInvoice.getUTCMonth())
         dateOfLastInvoice.setUTCDate(0)
      }
   } else {
      dateOfLastInvoice.setUTCFullYear(dateOfFutureInvoice.getUTCFullYear())
      dateOfLastInvoice.setUTCMonth(dateOfFutureInvoice.getUTCMonth() - 1)
      dateOfLastInvoice.setUTCDate(dateOfFutureInvoice.getUTCDate())
   }

   // Get first day of billing cycle
   let firstDay = dateOfLastInvoice
   // Get last day of the billing cycle
   let lastDay = dateOfFutureInvoice
   // Get total days in month
   let totalDaysInMonth = Math.round(
      (lastDay - firstDay) / (1000 * 60 * 60 * 24),
   )
   // Get days to be paid for, given specified activation date. This is activation date + every remaining day of the month
   let daysToPayAfterActivation =
      (lastDay - dateOfActivation) / (1000 * 60 * 60 * 24)

   return daysToPayAfterActivation / totalDaysInMonth == 1 ||
      daysToPayAfterActivation / totalDaysInMonth == 0
      ? false
      : true
}

export function getProductProratedPrice(
   activationDate,
   nextInvoiceDate,
   productPrice,
) {
   let priceToUse = productPrice
   if (priceToUse < 0) priceToUse = 0
   return getEstimatedProratedPrice(activationDate, nextInvoiceDate, priceToUse)
}

// Populates the proratedPrice (amount actually due for the prorated activation month)
// and proratedTotal (the remaining, unprorated portion of a full month) on a MonthlyPrice
// object. Only applies when the underlying pricebook product is billed monthly and flagged
// as IsProrated (matching the eligibility check used for the total due at activation), and
// when the quote itself is prorated. Otherwise both fields are reset to 0.
export function applyProrationToMonthlyPrice(
   monthlyPriceObj,
   product_list,
   activationDate,
   nextInvoiceDate,
   differenceQuote,
   account_status,
) {
   const productDef = product_list?.[monthlyPriceObj?.productCode]
   const isEligible =
      productDef?.BillingFreq === 'monthly' &&
      !!productDef?.IsProrated &&
      !productDef?.NotInPricebook

   if (
      !isEligible ||
      !isProrated(activationDate, nextInvoiceDate, differenceQuote, account_status)
   ) {
      monthlyPriceObj.proratedPrice = 0
      monthlyPriceObj.proratedTotal = 0
      return
   }

   // For difference/expansion quotes where the customer has an existing subscription
   // (previousTotalPrice > 0), only the incremental upsell (totalPriceDifference) is
   // actually billed during the prorated activation month — matching the diff-aware
   // "amount due at activation" calculation in calculateActivationTotalForProduct.mjs
   // (calculateActivationForDiff). Prorating the full totalPrice here would double-charge
   // for the customer's pre-existing subscription cost.
   // When there's no previous subscription to diff against (e.g. accountSnowflake has no
   // usable MRR data, or this product is new to the account), totalPriceDifference is left
   // at its default of 0 by calculateDifference() rather than falling back to totalPrice,
   // so guard on previousTotalPrice instead of blindly trusting totalPriceDifference.
   const chargeableAmount =
      differenceQuote && monthlyPriceObj.previousTotalPrice > 0
         ? monthlyPriceObj.totalPriceDifference
         : monthlyPriceObj.totalPrice

   const proratedPrice = parseFloat(
      getProductProratedPrice(
         activationDate,
         nextInvoiceDate,
         chargeableAmount,
      ),
   )
   monthlyPriceObj.proratedPrice = proratedPrice
   monthlyPriceObj.proratedTotal = monthlyPriceObj.totalPrice - proratedPrice
}

// Returns an object of details concerning a prorated price. Takes in an activation date and total price.
// Divides total cost by amount of days in billing cycle, then sums the per day cost + remaining days in the month inclusive of activation date.
// The billing cycle is inclusive on the date of last billing, until the night before the next billing date.
// For example, if the billing date for a company is the 20th of every month, each billing cycle runs from the 20th-19th of the next month.
function getEstimatedProratedPrice(
   activationDate,
   nextInvoiceDate,
   totalPrice,
) {
   // Date of activation of products.
   // All date math below uses the UTC getters/setters so that calendar dates parsed from
   // 'YYYY-MM-DD' strings (which Date() always parses as UTC midnight) aren't shifted by a
   // day when the host machine's local timezone is behind UTC.
   let dateOfActivation = new Date(activationDate)
   dateOfActivation.setUTCDate(dateOfActivation.getUTCDate())

   // Date of next invoice. Takes into account if the activation date is after the next invoice date. If so, uses the next months billing date for the calculation.
   let dateOfFutureInvoice = new Date(nextInvoiceDate)
   if (dateOfActivation > dateOfFutureInvoice) {
      dateOfFutureInvoice.setUTCMonth(
         dateOfFutureInvoice.getUTCMonth() +
            Math.max(
               1,
               dateOfActivation.getUTCMonth() -
                  dateOfFutureInvoice.getUTCMonth() +
                  dateOfActivation.getUTCDate() >
                  dateOfFutureInvoice.getUTCDate()
                  ? 1
                  : 0,
            ),
      )
   }

   // Find date of last invoice. If the day of the month of billing date is greater than the last day of the (I.E someone is billed on the 30th normally, but it is February and the onth only has 28 days), then use the last day of the month instead.
   let dateOfLastInvoice = new Date(dateOfFutureInvoice)
   if (
      new Date(
         Date.UTC(
            dateOfFutureInvoice.getUTCFullYear(),
            dateOfFutureInvoice.getUTCMonth() - 1,
            dateOfFutureInvoice.getUTCDate(),
         ),
      ) >
      new Date(
         Date.UTC(
            dateOfFutureInvoice.getUTCFullYear(),
            dateOfFutureInvoice.getUTCMonth() - 1,
         ),
      )
   ) {
      dateOfLastInvoice.setUTCFullYear(dateOfFutureInvoice.getUTCFullYear())
      dateOfLastInvoice.setUTCMonth(dateOfFutureInvoice.getUTCMonth() - 1)
      dateOfLastInvoice.setUTCDate(dateOfFutureInvoice.getUTCDate())
      // If the billing date would have been past the last day of the month (IE into the next month), set the billing date to the last day of the month.
      if (dateOfLastInvoice.getUTCMonth() == dateOfFutureInvoice.getUTCMonth()) {
         dateOfLastInvoice.setUTCMonth(dateOfLastInvoice.getUTCMonth())
         dateOfLastInvoice.setUTCDate(0)
      }
   } else {
      dateOfLastInvoice.setUTCFullYear(dateOfFutureInvoice.getUTCFullYear())
      dateOfLastInvoice.setUTCMonth(dateOfFutureInvoice.getUTCMonth() - 1)
      dateOfLastInvoice.setUTCDate(dateOfFutureInvoice.getUTCDate())
   }

   // Get first day of billing cycle
   let firstDay = dateOfLastInvoice
   // Get last day of the billing cycle
   let lastDay = dateOfFutureInvoice
   // Get total days in month
   let totalDaysInMonth = Math.round(
      (lastDay - firstDay) / (1000 * 60 * 60 * 24),
   )
   // Get days to be paid for, given specified activation date. This is activation date + every remaining day of the month
   let daysToPayAfterActivation =
      (lastDay - dateOfActivation) / (1000 * 60 * 60 * 24)

   return dateOfActivation == dateOfFutureInvoice
      ? 0
      : Math.max(
           ((totalPrice / totalDaysInMonth) * daysToPayAfterActivation).toFixed(
              2,
           ),
           0,
        )
}
